/**
 * Engagement - cálculo de score e nível (Low/Medium/High) a partir do BehaviorState.
 * Fórmula oficial: engagementScore = scrollWeight + clickWeight + timeWeight + interactionWeight (0–100).
 * Classificação: 0–30 Low, 31–70 Medium, 71+ High.
 */

import type { BehaviorState, EngagementLevel, EngagementResult } from './types';

const DEFAULT_WEIGHTS = {
  scroll: 0.3,
  click: 0.3,
  time: 0.25,
  interaction: 0.15,
};

/** Bandas oficiais: 0–30 Low, 31–70 Medium, 71+ High. */
const DEFAULT_THRESHOLDS = {
  low: 30,
  medium: 70,
};

export interface EngagementOptions {
  weights?: {
    scroll?: number;
    click?: number;
    time?: number;
    interaction?: number;
  };
  thresholds?: { low: number; medium: number };
  /** Opcional: keypresses e mouseMovements para interactionWeight. */
  interaction?: { keypresses: number; mouseMovements: number };
}

/** Normaliza scroll 0–100 para contribuição 0–1. */
function scrollContribution(maxScrollPercent: number): number {
  return Math.min(1, maxScrollPercent / 100);
}

/** Normaliza cliques (ex.: 10 cliques = 1, com cap). */
function clickContribution(clickCount: number, rageClickCount: number): number {
  const positive = Math.max(0, clickCount - rageClickCount * 3);
  return Math.min(1, (positive + rageClickCount) / 15);
}

/** Normaliza tempo ativo em ms (ex.: 5 min = 1). */
function timeContribution(activeMs: number): number {
  const fiveMin = 5 * 60 * 1000;
  return Math.min(1, activeMs / fiveMin);
}

/** Normaliza interação (keypresses + mouseMovements) para 0–1. */
function interactionContribution(
  keypresses: number,
  mouseMovements: number
): number {
  const combined = keypresses + mouseMovements;
  return Math.min(1, combined / 50);
}

/**
 * Calcula engagement score e nível a partir do estado de behavior.
 * Fórmula: scrollWeight + clickWeight + timeWeight + interactionWeight, normalizado 0–100.
 */
export function computeEngagement(
  state: BehaviorState,
  options?: EngagementOptions
): EngagementResult {
  const weights = { ...DEFAULT_WEIGHTS, ...options?.weights };
  const thresholds = { ...DEFAULT_THRESHOLDS, ...options?.thresholds };

  const scrollScore =
    scrollContribution(state.scroll.maxScrollPercent) * weights.scroll;
  const clickScore =
    clickContribution(state.click.clickCount, state.click.rageClickCount) *
    weights.click;
  const timeScore = timeContribution(state.idle.activeMs) * weights.time;

  const interaction = options?.interaction ?? {
    keypresses: 0,
    mouseMovements: 0,
  };
  const interactionScore =
    interactionContribution(
      interaction.keypresses,
      interaction.mouseMovements
    ) * weights.interaction;

  const score = Math.round(
    (scrollScore + clickScore + timeScore + interactionScore) * 100
  );

  let level: EngagementLevel = 'low';
  if (score >= thresholds.medium) level = 'high';
  else if (score > thresholds.low) level = 'medium';

  return { score, level };
}
